import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook d'inactivité réutilisable.
 *
 * Démarre / redémarre un minuteur à chaque activité utilisateur
 * (souris, clavier, scroll, touch). Quand aucune activité n'est
 * détectée pendant `timeoutMs`, la fonction `onTimeout` est appelée.
 *
 * Exemple d'usage :
 *   useInactivityTimeout({
 *     onTimeout: () => history.push('/'),
 *     timeoutMs: 15 * 60 * 1000
 *   });
 *
 * @param {Object} options
 * @param {() => void} options.onTimeout - Appelé lorsque le délai d'inactivité est atteint.
 * @param {number} [options.timeoutMs=900000] - Délai d'inactivité en ms (défaut 15 minutes).
 * @param {boolean} [options.enabled=true] - Si false, le minuteur est désactivé.
 */
export function useInactivityTimeout({
  onTimeout,
  timeoutMs = 15 * 60 * 1000,
  enabled = true,
}) {
  const timeoutIdRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const clearTimer = useCallback(() => {
    if (timeoutIdRef.current != null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    if (!enabled || typeof onTimeoutRef.current !== 'function') {
      return;
    }
    timeoutIdRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, timeoutMs);
  }, [clearTimer, enabled, timeoutMs]);

  const handleActivity = useCallback(() => {
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      return;
    }

    // Événements considérés comme activité utilisateur
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

    startTimer();
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity);
    });

    return () => {
      clearTimer();
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, [enabled, handleActivity, clearTimer, startTimer]);
}

